package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.billing.PlanCheck
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.rosterimport.CellKey
import com.evoapps.evoshift.domain.rosterimport.GetRosterImportContextUseCase
import com.evoapps.evoshift.domain.rosterimport.ImportTeamMember
import com.evoapps.evoshift.domain.rosterimport.NameMatch
import com.evoapps.evoshift.domain.rosterimport.NameMatcher
import com.evoapps.evoshift.domain.rosterimport.PersonChoice
import com.evoapps.evoshift.domain.rosterimport.RosterCodeAction
import com.evoapps.evoshift.domain.rosterimport.RosterCodeDictionary
import com.evoapps.evoshift.domain.rosterimport.RosterCodes
import com.evoapps.evoshift.domain.rosterimport.RosterImportContext
import com.evoapps.evoshift.domain.rosterimport.RosterImportLayout
import com.evoapps.evoshift.domain.rosterimport.RosterImportPlan
import com.evoapps.evoshift.domain.rosterimport.RosterImportPlanner
import com.evoapps.evoshift.domain.rosterimport.SaveRosterCodesUseCase
import com.evoapps.evoshift.domain.spreadsheet.Spreadsheet
import com.evoapps.evoshift.domain.spreadsheet.SpreadsheetFormat
import com.evoapps.evoshift.domain.spreadsheet.SpreadsheetReader
import com.evoapps.evoshift.domain.staffimport.ImportEmployeeUseCase
import com.evoapps.evoshift.domain.staffimport.StaffCandidate
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.CheckSeatsUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.roster.CopyOpenReason
import com.evoapps.evoshift.domain.usecase.roster.CopyPreview
import com.evoapps.evoshift.domain.usecase.roster.CopyShiftsUseCase
import com.evoapps.evoshift.domain.usecase.roster.PlannedDaysMode
import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.spreadsheet.PickedFile
import com.evoapps.evoshift.saves.PendingSaves
import com.evoapps.evoshift.saves.SaveKind
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.roster_import_header_below_first_day
import evoshift.composeapp.generated.resources.roster_import_names_and_days_same_column
import evoshift.composeapp.generated.resources.roster_import_nothing_found
import evoshift.composeapp.generated.resources.roster_import_undone
import evoshift.composeapp.generated.resources.sales_import_file_unreadable
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import kotlinx.datetime.LocalDate

/** The import's steps: the cells, where the roster is on them, the codes, the people, the check, the outcome. */
enum class RosterImportStep { SOURCE, LAYOUT, CODES, PEOPLE, PREVIEW, DONE }

/** What a tap on the sheet sets while pointing at the layout. */
enum class LayoutTarget { NAMES, FIRST_DAY, HEADER }

/** A code of the sheet in the dictionary step. */
data class RosterImportCodeRow(
    val code: String,
    val count: Int,
    /** What it stands for (null: not decided, its cells are problems). */
    val action: RosterCodeAction?,
    /** Filled in from the workplace's dictionary (an earlier import). */
    val fromDictionary: Boolean = false,
    /** Filled in as a common code ("L4", "W"…) or a template's name; the person confirms it. */
    val suggested: Boolean = false
)

/** A person's row of the sheet in the people step. */
data class RosterImportPersonRow(
    val row: Int,
    val name: String,
    val choice: PersonChoice,
    val match: NameMatch,
    /** Filled day cells in the row (what the person would get). */
    val cells: Int
)

/** What the import did (the outcome step, with Undo). */
data class RosterImportSummary(
    val created: Int,
    /** Shifts created without their person, by why (absent, no longer works here…). */
    val withoutPerson: Map<CopyOpenReason, Int>,
    /** Shifts not created because their day is closed (Sunday trading, a holiday). */
    val skippedClosed: Int,
    /** Shifts not created because their day already had shifts (Skip). */
    val skippedPlanned: Int,
    /** Shifts removed first (Replace). */
    val replaced: Int,
    val newPeople: Int,
    /** Absence days left free (the import creates no absences). */
    val absenceDays: Int
)

/** A row's absence days, listed because the import does not create absences. */
data class RosterImportAbsences(val name: String, val days: List<Pair<AbsenceType, LocalDate>>)

data class RosterImportUiState(
    /** Only until it is known who looks and what the month is. */
    val isLoading: Boolean = true,
    val capabilities: Set<Capability> = emptySet(),
    val status: RosterStatus? = null,
    val year: Int = 0,
    val month: Int = 0,
    val templates: List<ShiftTemplate> = emptyList(),
    val team: List<ImportTeamMember> = emptyList(),
    val loadError: UiText? = null,
    // --- The source ---
    val pasted: String = "",
    val isReading: Boolean = false,
    val fileName: String? = null,
    val format: SpreadsheetFormat? = null,
    val sheetNames: List<String> = emptyList(),
    val sheetIndex: Int = 0,
    val step: RosterImportStep = RosterImportStep.SOURCE,
    // --- The layout ---
    val rows: List<List<String>> = emptyList(),
    val target: LayoutTarget = LayoutTarget.NAMES,
    val nameColumn: Int? = null,
    val firstCell: CellKey? = null,
    /** The date of the first day's cell: the month's day (1 by default). */
    val firstDay: Int = 1,
    val headerRow: Int? = null,
    // --- The codes, the people, the check ---
    val plan: RosterImportPlan? = null,
    val codes: List<RosterImportCodeRow> = emptyList(),
    val people: List<RosterImportPersonRow> = emptyList(),
    /** The cell being fixed. */
    val editingCell: CellKey? = null,
    val plannedDays: PlannedDaysMode = PlannedDaysMode.SKIP,
    /** Days the import puts shifts on that already have shifts, with how many. */
    val daysWithShifts: Map<LocalDate, Int> = emptyMap(),
    /** The copy tool's check of what would be written (people who are away, closed days…). */
    val check: CopyPreview? = null,
    val isChecking: Boolean = false,
    // --- Importing ---
    val isImporting: Boolean = false,
    val summary: RosterImportSummary? = null,
    val isUndoing: Boolean = false,
    val undone: Boolean = false,
    val planLimit: PlanCheck.LimitReached? = null,
    val canChoosePlan: Boolean = false,
    val message: UiText? = null
) {
    /** Importing a month: owners and managers, a month being built (not locked), not once the plan ended. */
    val canImport: Boolean
        get() = capabilities.shows(Gate.ROSTER_EDITING) && (status == RosterStatus.DRAFT || status == RosterStatus.PUBLISHED)

    val layoutComplete: Boolean get() = nameColumn != null && firstCell != null

    /** The month's last day. */
    val monthLength: Int get() = if (year == 0) 31 else (28..31).last { d -> runCatching { LocalDate(year, month, d) }.isSuccess }

    /** People rows that are imported (decided and not skipped). */
    val importedRows: Set<Int> get() = people.filter { it.choice is PersonChoice.Member || it.choice == PersonChoice.NewPerson }.map { it.row }.toSet()
    val undecidedPeople: Int get() = people.count { it.choice == PersonChoice.Undecided }
    val newPeople: Int get() = people.count { it.choice == PersonChoice.NewPerson }
    val undecidedCodes: Int get() = codes.count { it.action == null }

    /** Problem cells of imported rows still to fix or skip. */
    val problems: Int get() = plan?.cells?.count { it.needsFix && it.key.row in importedRows } ?: 0
    val skippedCells: Int get() = plan?.cells?.count { it.skipped && it.key.row in importedRows } ?: 0
    /** Shifts the import would create (before the copy tool's checks). */
    val shiftsToCreate: Int
        get() = plan?.cells?.filter { !it.skipped && it.key.row in importedRows }
            ?.sumOf { (it.value as? com.evoapps.evoshift.domain.rosterimport.CellValue.Shifts)?.ranges?.size ?: 0 } ?: 0

    val absences: List<RosterImportAbsences>
        get() {
            val plan = plan ?: return emptyList()
            val names = people.associate { it.row to it.name }
            return RosterImportPlanner.absences(plan, importedRows).map { (row, days) -> RosterImportAbsences(names[row].orEmpty(), days) }
        }

    val editing: com.evoapps.evoshift.domain.rosterimport.ImportCell? get() = editingCell?.let { key -> plan?.cell(key) }

    /** The draft keys the copy tool leaves without their person, by cell. */
    val withoutPerson: Map<CellKey, CopyOpenReason>
        get() = check?.shifts.orEmpty().mapNotNull { copied ->
            val reason = copied.leftOpen ?: return@mapNotNull null
            cellOf(copied.sourceShiftId)?.let { it to reason }
        }.toMap()

    val canConfirm: Boolean get() = canImport && !isImporting && problems == 0 && undecidedPeople == 0 && shiftsToCreate > 0

    companion object {
        /** The cell of a draft key ("import-r3-c5-0"). */
        fun cellOf(key: String): CellKey? {
            val parts = key.removePrefix("import-").split('-')
            if (parts.size < 2) return null
            val row = parts[0].removePrefix("r").toIntOrNull() ?: return null
            val column = parts[1].removePrefix("c").toIntOrNull() ?: return null
            return CellKey(row, column)
        }
    }
}

/**
 * SH-217: the roster builder's "Import the roster from a spreadsheet" (owners and managers, a
 * month being built). Cells pasted from Excel or Google Sheets, or a .xlsx/.csv file picked or
 * dropped, read on the device ([SpreadsheetReader], shared with SH-214). Nothing is guessed: the
 * person points at the column with the names and the first day's cell (and, optionally, a row with
 * day numbers); every code that is not a time is listed once and given a meaning (the workplace's
 * dictionary fills in what an earlier import learned); names are matched to the team
 * ([NameMatcher]) or added as new people (the staff import's path) or skipped; the month is shown
 * with its problem cells to fix or skip; then the shifts are written through the copy tool
 * ([CopyShiftsUseCase.previewImport]: Add / Replace / Skip for days with shifts, the same people
 * checks, one Undo for the whole import). Nothing is written before "Import".
 */
class RosterImportViewModel(
    private val rosterId: String,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getContext: GetRosterImportContextUseCase,
    private val saveCodes: SaveRosterCodesUseCase,
    private val importEmployee: ImportEmployeeUseCase,
    private val copyShifts: CopyShiftsUseCase,
    private val checkSeats: CheckSeatsUseCase? = null,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    /**
     * SH-260: the app's saves. The month's costs and daily records follow an import (and its
     * undo) there, with nobody waiting: the dialog shows Done at once and the work finishes even
     * if it is closed ("Recalculating costs…"; a failure is reported and offered again).
     */
    private val saves: PendingSaves = PendingSaves(),
    /** Where files are read (off the main thread; tests read them in place). */
    private val parsing: CoroutineDispatcher = Dispatchers.Default
) : ViewModel() {

    private val workplaceId = RosterIds.workplaceIdOf(rosterId)
    private val _uiState = MutableStateFlow(RosterImportUiState())
    val uiState: StateFlow<RosterImportUiState> = _uiState.asStateFlow()

    private var spreadsheet: Spreadsheet? = null
    private var context: RosterImportContext? = null
    private var editorId = ""
    private val edits = mutableMapOf<CellKey, String>()
    private val skipped = mutableSetOf<CellKey>()
    /** What the import wrote, for Undo. */
    private var written: List<String> = emptyList()
    private var replaced: List<Shift> = emptyList()
    private val checkJob = JobSlot()
    /** The person chose Skip / Add / Replace themselves (else it follows the days, see [toPreview]). */
    private var plannedDaysChosen = false

    init {
        viewModelScope.launch { load() }
    }

    private suspend fun load() {
        val me = getCurrentUser().first()
        editorId = me?.uid.orEmpty()
        val capabilities = me?.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId)).orEmpty()
        if (!capabilities.shows(Gate.ROSTER_EDITING)) {
            _uiState.update { it.copy(isLoading = false, capabilities = capabilities) }
            return
        }
        getContext(rosterId).fold(
            onSuccess = { found ->
                context = found
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        capabilities = capabilities,
                        canChoosePlan = capabilities.shows(Gate.PLAN),
                        status = found.roster.status,
                        year = found.roster.year,
                        month = found.roster.month,
                        templates = found.templates,
                        team = found.team,
                        loadError = null
                    )
                }
            },
            onFailure = { e -> _uiState.update { it.copy(isLoading = false, capabilities = capabilities, loadError = e.toUiText()) } }
        )
    }

    fun retry() {
        _uiState.update { it.copy(isLoading = true, loadError = null) }
        viewModelScope.launch { load() }
    }

    // --- The source --------------------------------------------------------------------------

    fun onPastedChanged(text: String) = _uiState.update { it.copy(pasted = text) }

    fun readPasted() {
        val text = _uiState.value.pasted
        if (!_uiState.value.canImport || text.isBlank()) return
        runCatching { SpreadsheetReader.readPasted(text) }.fold(
            onSuccess = { opened(it, fileName = null) },
            onFailure = { e -> _uiState.update { it.copy(message = e.toUiText()) } }
        )
    }

    /** A file picked or dropped (null: it could not be read). */
    fun onFile(file: PickedFile?) {
        val state = _uiState.value
        if (!state.canImport || state.isImporting) return
        if (file == null) {
            _uiState.update { it.copy(message = UiText.Resource(Res.string.sales_import_file_unreadable)) }
            return
        }
        _uiState.update { it.copy(isReading = true, fileName = file.name) }
        viewModelScope.launch {
            runCatching { withContext(parsing) { SpreadsheetReader.read(file.bytes) } }.fold(
                onSuccess = { opened(it, file.name) },
                onFailure = { e -> _uiState.update { it.copy(isReading = false, fileName = null, message = e.toUiText()) } }
            )
        }
    }

    private fun opened(file: Spreadsheet, fileName: String?) {
        spreadsheet = file
        val sheet = file.firstFilledSheet
        _uiState.update {
            it.copy(
                isReading = false,
                fileName = fileName,
                format = file.format,
                sheetNames = file.sheets.map { s -> s.name },
                sheetIndex = sheet,
                step = RosterImportStep.LAYOUT
            )
        }
        resetLayout(sheet)
    }

    /** Another sheet of the workbook: the layout is pointed at afresh. */
    fun selectSheet(index: Int) {
        val file = spreadsheet ?: return
        if (index !in file.sheets.indices || _uiState.value.isImporting) return
        _uiState.update { it.copy(sheetIndex = index) }
        resetLayout(index)
    }

    private fun resetLayout(sheet: Int) {
        edits.clear()
        skipped.clear()
        _uiState.update {
            it.copy(
                rows = spreadsheet?.sheets?.getOrNull(sheet)?.rows.orEmpty(),
                target = LayoutTarget.NAMES, nameColumn = null, firstCell = null, firstDay = 1, headerRow = null,
                plan = null, codes = emptyList(), people = emptyList(), check = null, summary = null, undone = false
            )
        }
    }

    // --- The layout --------------------------------------------------------------------------

    fun setTarget(target: LayoutTarget) = _uiState.update { it.copy(target = target) }

    /** A tap on the sheet's cell [row], [column]: sets what [RosterImportUiState.target] points at. */
    fun onCellTapped(row: Int, column: Int) {
        val state = _uiState.value
        if (state.step != RosterImportStep.LAYOUT) return
        when (state.target) {
            LayoutTarget.NAMES -> {
                if (state.firstCell?.column == column) {
                    _uiState.update { it.copy(message = UiText.Resource(Res.string.roster_import_names_and_days_same_column)) }
                    return
                }
                _uiState.update { it.copy(nameColumn = column, target = if (it.firstCell == null) LayoutTarget.FIRST_DAY else it.target) }
            }
            LayoutTarget.FIRST_DAY -> {
                if (state.nameColumn == column) {
                    _uiState.update { it.copy(message = UiText.Resource(Res.string.roster_import_names_and_days_same_column)) }
                    return
                }
                // A header row at or below the first day no longer fits.
                _uiState.update { it.copy(firstCell = CellKey(row, column), headerRow = it.headerRow?.takeIf { h -> h < row }) }
            }
            LayoutTarget.HEADER -> {
                val first = state.firstCell
                if (first != null && row >= first.row) {
                    _uiState.update { it.copy(message = UiText.Resource(Res.string.roster_import_header_below_first_day)) }
                    return
                }
                _uiState.update { it.copy(headerRow = if (it.headerRow == row) null else row) }
            }
        }
    }

    fun clearHeaderRow() = _uiState.update { it.copy(headerRow = null) }

    /** The first day's cell is the month's day [day]. */
    fun setFirstDay(day: Int) {
        val state = _uiState.value
        if (day !in 1..state.monthLength) return
        _uiState.update { it.copy(firstDay = day) }
    }

    private fun layout(state: RosterImportUiState = _uiState.value): RosterImportLayout? {
        val nameColumn = state.nameColumn ?: return null
        val first = state.firstCell ?: return null
        if (state.year == 0) return null
        return RosterImportLayout(
            nameColumn = nameColumn,
            firstRow = first.row,
            firstDayColumn = first.column,
            firstDate = LocalDate(state.year, state.month, state.firstDay),
            headerRow = state.headerRow?.takeIf { it < first.row }
        )
    }

    /** The layout as pointed at: the codes next (or the people, when the sheet has only times). */
    fun confirmLayout() {
        val state = _uiState.value
        val context = context ?: return
        val layout = layout(state) ?: return
        edits.clear()
        skipped.clear()
        plannedDaysChosen = false
        val plan = RosterImportPlanner.plan(state.rows, layout, state.year, state.month, context.dictionary, context.templates)
        if (plan.people.isEmpty() || plan.cells.isEmpty()) {
            _uiState.update { it.copy(message = UiText.Resource(Res.string.roster_import_nothing_found)) }
            return
        }
        val codes = plan.codes.map { use ->
            val saved = context.dictionary[use.code]?.takeIf { usable(it, context.templates) }
            val suggestion = if (saved == null) RosterCodes.suggestion(use.code, context.templates) else null
            RosterImportCodeRow(use.code, use.count, saved ?: suggestion, fromDictionary = saved != null, suggested = suggestion != null)
        }
        val people = plan.people.map { person ->
            val match = NameMatcher.match(person.name, context.team)
            val choice = when (match) {
                is NameMatch.Exact -> PersonChoice.Member(match.uid)
                is NameMatch.Likely -> PersonChoice.Member(match.uid)
                else -> PersonChoice.Undecided
            }
            RosterImportPersonRow(person.row, person.name, choice, match, plan.cellsOf(person.row).size)
        }
        _uiState.update {
            it.copy(
                codes = codes, people = people, plannedDays = PlannedDaysMode.SKIP,
                step = if (codes.isEmpty()) RosterImportStep.PEOPLE else RosterImportStep.CODES
            )
        }
        replan()
    }

    private fun usable(action: RosterCodeAction, templates: List<ShiftTemplate>): Boolean =
        action !is RosterCodeAction.Template || templates.any { it.id == action.templateId }

    fun backToLayout() {
        if (_uiState.value.isImporting) return
        _uiState.update { it.copy(step = RosterImportStep.LAYOUT, editingCell = null) }
    }

    // --- The codes ---------------------------------------------------------------------------

    /** [code] stands for [action] (null: not decided). */
    fun setCodeAction(code: String, action: RosterCodeAction?) {
        _uiState.update { s ->
            s.copy(codes = s.codes.map { if (it.code == code) it.copy(action = action, suggested = false, fromDictionary = false) else it })
        }
        replan()
    }

    fun toPeople() {
        if (_uiState.value.isImporting) return
        _uiState.update { it.copy(step = RosterImportStep.PEOPLE) }
    }

    fun backToCodes() {
        if (_uiState.value.isImporting || _uiState.value.codes.isEmpty()) return
        _uiState.update { it.copy(step = RosterImportStep.CODES, editingCell = null) }
    }

    // --- The people --------------------------------------------------------------------------

    fun setPersonChoice(row: Int, choice: PersonChoice) {
        if (_uiState.value.isImporting) return
        if (choice is PersonChoice.Member && _uiState.value.team.none { it.uid == choice.uid }) return
        _uiState.update { s -> s.copy(people = s.people.map { if (it.row == row) it.copy(choice = choice) else it }) }
        replan()
    }

    /** "Add all unmatched as new people". */
    fun addAllUndecidedAsNew() {
        _uiState.update { s -> s.copy(people = s.people.map { if (it.choice == PersonChoice.Undecided) it.copy(choice = PersonChoice.NewPerson) else it }) }
        replan()
    }

    fun toPreview() {
        val state = _uiState.value
        if (state.isImporting || state.undecidedPeople > 0) return
        // Days that hold only the skeleton's unassigned shifts are replaced by default (the month
        // was created from the skeleton); with someone's shift on them, nothing is touched unless asked.
        val context = context
        val default = if (context != null && state.daysWithShifts.isNotEmpty() && state.daysWithShifts.keys.all { (context.assignedPerDay[it] ?: 0) == 0 })
            PlannedDaysMode.REPLACE else PlannedDaysMode.SKIP
        _uiState.update { it.copy(step = RosterImportStep.PREVIEW, plannedDays = if (plannedDaysChosen) it.plannedDays else default) }
        refreshCheck()
    }

    fun backToPeople() {
        if (_uiState.value.isImporting) return
        _uiState.update { it.copy(step = RosterImportStep.PEOPLE, editingCell = null) }
    }

    // --- The preview -------------------------------------------------------------------------

    fun startEdit(key: CellKey) {
        if (_uiState.value.isImporting || _uiState.value.plan?.cell(key) == null) return
        _uiState.update { it.copy(editingCell = key) }
    }

    fun cancelEdit() = _uiState.update { it.copy(editingCell = null) }

    /** The cell as fixed: read again (a time, or a code with its meaning). */
    fun saveEdit(text: String) {
        val key = _uiState.value.editingCell ?: return
        val original = _uiState.value.rows.getOrNull(key.row)?.getOrNull(key.column).orEmpty().trim()
        if (text.trim() == original) edits.remove(key) else edits[key] = text.trim()
        skipped.remove(key)
        _uiState.update { it.copy(editingCell = null) }
        replan()
        refreshCheck()
    }

    /** "Skip" / "Include again" a cell. */
    fun toggleSkip(key: CellKey) {
        if (_uiState.value.isImporting) return
        if (key in skipped) skipped.remove(key) else skipped += key
        _uiState.update { it.copy(editingCell = null) }
        replan()
        refreshCheck()
    }

    /** "Skip all problem cells": they stay shown and can be brought back. */
    fun skipAllProblems() {
        val state = _uiState.value
        if (state.isImporting) return
        state.plan?.cells?.filter { it.needsFix && it.key.row in state.importedRows }?.forEach { skipped += it.key }
        replan()
        refreshCheck()
    }

    fun setPlannedDays(mode: PlannedDaysMode) {
        if (_uiState.value.isImporting) return
        plannedDaysChosen = true
        _uiState.update { it.copy(plannedDays = mode) }
        refreshCheck()
    }

    private fun dictionaryOf(state: RosterImportUiState): RosterCodeDictionary =
        RosterCodeDictionary(state.codes.mapNotNull { row -> row.action?.let { row.code to it } }.toMap())

    /** The plan again with the codes, fixes and skips (a fix may bring a code the list lacks: it joins the list). */
    private fun replan() {
        val state = _uiState.value
        val context = context ?: return
        val layout = layout(state) ?: return
        val plan = RosterImportPlanner.plan(state.rows, layout, state.year, state.month, dictionaryOf(state), context.templates, edits, skipped)
        val known = state.codes.associateBy { it.code }
        val codes = plan.codes.map { use ->
            known[use.code]?.copy(count = use.count) ?: run {
                val saved = context.dictionary[use.code]?.takeIf { usable(it, context.templates) }
                val suggestion = if (saved == null) RosterCodes.suggestion(use.code, context.templates) else null
                RosterImportCodeRow(use.code, use.count, saved ?: suggestion, fromDictionary = saved != null, suggested = suggestion != null)
            }
        }
        val withCodes = state.copy(codes = codes)
        // A code learned from a fix gives its meaning at once.
        val replanned = if (codes.any { it.code !in known && it.action != null }) {
            RosterImportPlanner.plan(state.rows, layout, state.year, state.month, dictionaryOf(withCodes), context.templates, edits, skipped)
        } else plan
        val imported = withCodes.importedRows
        val days = replanned.cells.filter { !it.skipped && it.key.row in imported && it.value is com.evoapps.evoshift.domain.rosterimport.CellValue.Shifts }
            .map { it.date }.toSet()
        _uiState.update {
            it.copy(
                plan = replanned,
                codes = codes,
                people = it.people.map { p -> p.copy(cells = replanned.cellsOf(p.row).size) },
                daysWithShifts = context.shiftsPerDay.filterKeys { day -> day in days }
            )
        }
    }

    /** The people's ids for the drafts: members as they are, new people as placeholders until added. */
    private fun peopleIds(state: RosterImportUiState, added: Map<Int, String> = emptyMap()): Map<Int, Pair<String, String>> =
        state.people.mapNotNull { person ->
            when (val choice = person.choice) {
                is PersonChoice.Member -> person.row to (choice.uid to (state.team.firstOrNull { it.uid == choice.uid }?.displayName ?: person.name))
                PersonChoice.NewPerson -> person.row to ((added[person.row] ?: "$NEW_PERSON_PREFIX${person.row}") to person.name)
                else -> null
            }
        }.toMap()

    private fun drafts(state: RosterImportUiState, added: Map<Int, String> = emptyMap()) = state.plan?.let { plan ->
        RosterImportPlanner.drafts(plan, rosterId, peopleIds(state, added), state.templates, editorId.ifEmpty { CopyShiftsUseCase.CREATED_BY }, AppClock.now().toString())
    }.orEmpty()

    /** The copy tool's check of the drafts (nothing written): who is away, closed days, Replace. */
    private fun refreshCheck() {
        val state = _uiState.value
        if (state.step != RosterImportStep.PREVIEW) return
        val drafts = drafts(state)
        val placeholders = state.people.filter { it.choice == PersonChoice.NewPerson }.map { "$NEW_PERSON_PREFIX${it.row}" }.toSet()
        _uiState.update { it.copy(isChecking = true) }
        checkJob.launch(viewModelScope) {
            val result = copyShifts.previewImport(rosterId, drafts.map { it.key to it.shift }, state.plannedDays, newPeople = placeholders)
            _uiState.update { s ->
                result.fold(
                    onSuccess = { s.copy(check = it, isChecking = false) },
                    onFailure = { e -> s.copy(check = null, isChecking = false, message = e.toUiText()) }
                )
            }
        }
    }

    // --- Import and undo ---------------------------------------------------------------------

    /**
     * "Import": the new people first (within the plan's seats), then the code dictionary for next
     * time, then the shifts in one write through the copy tool.
     */
    fun import() {
        val state = _uiState.value
        val context = context ?: return
        if (!state.canConfirm) return
        _uiState.update { it.copy(isImporting = true, planLimit = null, editingCell = null) }
        viewModelScope.launch {
            val newRows = state.people.filter { it.choice == PersonChoice.NewPerson }
            if (newRows.isNotEmpty()) {
                val seats = checkSeats?.invoke(workplaceId, adding = newRows.size)
                if (seats is PlanCheck.LimitReached) {
                    _uiState.update { it.copy(isImporting = false, planLimit = seats) }
                    return@launch
                }
            }
            val added = mutableMapOf<Int, String>()
            for (person in newRows) {
                val result = importEmployee(
                    workplaceId = workplaceId,
                    workplaceName = context.workplaceName,
                    candidate = StaffCandidate(displayName = person.name),
                    includeSensitive = false,
                    inviter = null,
                    // They work the month on the sheet: from its first day (never later than today).
                    startDate = minOf(LocalDate(state.year, state.month, 1), AppClock.today()).toString()
                )
                val uid = result.getOrElse { e ->
                    // Those added so far stay in the team: they become members, so a retry adds nobody twice.
                    keepAdded(added)
                    _uiState.update { it.copy(isImporting = false, message = e.toUiText()) }
                    return@launch
                }.userId
                added[person.row] = uid
            }
            keepAdded(added)
            // The dictionary is a convenience: a failure to keep it never stops the import.
            saveCodes(workplaceId, dictionaryOf(_uiState.value), editorId)
            val now = _uiState.value
            val drafts = drafts(now, added)
            val written = copyShifts.previewImport(rosterId, drafts.map { it.key to it.shift }, now.plannedDays, newPeople = added.values.toSet())
                .mapCatching { preview -> preview to copyShifts.apply(preview).getOrThrow() }
            written.fold(
                onSuccess = { (preview, ids) ->
                    this@RosterImportViewModel.written = ids
                    replaced = preview.replaced
                    recalculate()
                    _uiState.update {
                        it.copy(
                            isImporting = false,
                            step = RosterImportStep.DONE,
                            check = preview,
                            undone = false,
                            summary = RosterImportSummary(
                                created = ids.size,
                                withoutPerson = preview.leftOpen,
                                skippedClosed = preview.skippedClosed,
                                skippedPlanned = preview.skippedPlanned,
                                replaced = preview.replaced.size,
                                newPeople = added.size,
                                absenceDays = it.absences.sumOf { a -> a.days.size }
                            )
                        )
                    }
                },
                onFailure = { e -> _uiState.update { it.copy(isImporting = false, message = e.toUiText()) } }
            )
        }
    }

    /** New people added: from now on members (the team knows them). */
    private fun keepAdded(added: Map<Int, String>) {
        if (added.isEmpty()) return
        val newcomers = _uiState.value.people.filter { it.row in added }.map { ImportTeamMember(added.getValue(it.row), it.name) }
        context = context?.let { c -> c.copy(team = c.team + newcomers) }
        _uiState.update { s ->
            s.copy(
                team = s.team + newcomers,
                people = s.people.map { p -> added[p.row]?.let { p.copy(choice = PersonChoice.Member(it)) } ?: p }
            )
        }
    }

    /** Undo: the import's shifts go (unless published meanwhile) and what Replace removed comes back. */
    fun undo() {
        val state = _uiState.value
        if (state.summary == null || state.undone || state.isUndoing || written.isEmpty()) return
        _uiState.update { it.copy(isUndoing = true) }
        viewModelScope.launch {
            copyShifts.undo(rosterId, written, replaced).fold(
                onSuccess = {
                    written = emptyList()
                    replaced = emptyList()
                    recalculate()
                    _uiState.update { it.copy(isUndoing = false, undone = true, message = UiText.Resource(Res.string.roster_import_undone)) }
                },
                onFailure = { e -> _uiState.update { it.copy(isUndoing = false, message = e.toUiText()) } }
            )
        }
    }

    /**
     * SH-260: the month's costs and daily records after the shifts are written, in the app's
     * scope and queued behind other repricing of the workplace (the last change wins). The import
     * never waits for them and is never undone by their failure.
     */
    private fun recalculate() {
        saves.launch(repriceSaveKey(workplaceId), SaveKind.RECALCULATE, queue = true) { copyShifts.recalculate(rosterId) }
    }

    /** Back to the start (another file or paste). */
    fun startOver() {
        if (_uiState.value.isImporting) return
        spreadsheet = null
        edits.clear()
        skipped.clear()
        _uiState.update {
            it.copy(
                step = RosterImportStep.SOURCE, fileName = null, format = null, sheetNames = emptyList(), sheetIndex = 0, rows = emptyList(),
                nameColumn = null, firstCell = null, headerRow = null, firstDay = 1, plan = null, codes = emptyList(), people = emptyList(),
                check = null, summary = null, undone = false, editingCell = null
            )
        }
    }

    fun planLimitShown() = _uiState.update { it.copy(planLimit = null) }

    fun messageShown() = _uiState.update { it.copy(message = null) }

    companion object {
        private const val NEW_PERSON_PREFIX = "new-person-"
    }
}
